import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, AlertCircle } from 'lucide-react';
import axiosInstance from '../../services/axiosInstance';
import AssistantMessage from './AssistantMessage';
import { useAuth } from '../../contexts/AuthContext';

interface ToolCall {
  name: string;
  input: Record<string, any>;
  result: any;
}

interface MessageData {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCall[];
  timestamp: string;
  usage?: { input_tokens: number; output_tokens: number };
  model?: string;
}

interface AssistantChatProps {
  mode: 'bubble' | 'full';
  conversationId: string | null;
  onConversationCreated?: (id: string) => void;
}

const AssistantChat: React.FC<AssistantChatProps> = ({ mode, conversationId, onConversationCreated }) => {
  const { user: authUser } = useAuth();
  const userName = authUser?.profile?.firstName || authUser?.username || '';
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const currentConvIdRef = useRef<string | null>(conversationId);
  // Conversazione appena creata da questo componente: non va ricaricata dal server, che salva la domanda
  // solo insieme alla risposta (la ricarica la cancellerebbe dallo schermo insieme all'attesa)
  const appenaCreataRef = useRef<string | null>(null);
  // Conversazione che aspetta la risposta: l'indicatore «sta pensando» compare solo lì
  const [inAttesaPer, setInAttesaPer] = useState<string | null>(null);

  // Keep ref in sync
  useEffect(() => {
    currentConvIdRef.current = conversationId;
  }, [conversationId]);

  // Load conversation messages when conversationId changes
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    if (appenaCreataRef.current === conversationId) {
      appenaCreataRef.current = null;
      return;
    }

    let annullato = false;
    const loadConversation = async () => {
      setLoadingConversation(true);
      try {
        const res = await axiosInstance.get(`/admin/assistant/conversations/${conversationId}`);
        if (!annullato && res.data.success) {
          setMessages(res.data.data.messages || []);
        }
      } catch (err: any) {
        if (!annullato) setError('Errore nel caricamento della conversazione');
      } finally {
        if (!annullato) setLoadingConversation(false);
      }
    };

    loadConversation();
    return () => { annullato = true; };
  }, [conversationId]);

  // Auto-scroll to bottom (only when there are messages)
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setError(null);
    setInput('');
    setLoading(true);

    // Add user message optimistically
    const userMsg: MessageData = {
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInAttesaPer(currentConvIdRef.current);

    try {
      let activeConvId = currentConvIdRef.current;

      // Create conversation if none exists
      if (!activeConvId) {
        const createRes = await axiosInstance.post('/admin/assistant/conversations', {
          title: trimmed.substring(0, 80),
        });
        if (createRes.data.success) {
          activeConvId = createRes.data.data._id;
          currentConvIdRef.current = activeConvId;
          appenaCreataRef.current = activeConvId;
          setInAttesaPer(activeConvId);
          onConversationCreated?.(activeConvId!);
        } else {
          throw new Error('Errore nella creazione della conversazione');
        }
      }

      // Send message
      const res = await axiosInstance.post(`/admin/assistant/conversations/${activeConvId}/messages`, {
        content: trimmed,
      }, {
        timeout: 120000, // 2 minuti — le risposte AI con tool calls possono essere lente
      });

      if (res.data.success) {
        const assistantMsg: MessageData = {
          role: 'assistant',
          content: res.data.data.message.content,
          toolCalls: res.data.data.message.toolCalls,
          timestamp: res.data.data.message.timestamp,
          usage: res.data.data.usage || undefined,
          model: res.data.data.model || undefined,
        };
        // Se nel frattempo si è aperta un'altra conversazione la risposta resta salvata e si vede tornandoci
        if (currentConvIdRef.current === activeConvId) setMessages(prev => [...prev, assistantMsg]);
      } else {
        throw new Error(res.data.error || 'Errore nella risposta');
      }
    } catch (err: any) {
      const errMsg = err.code === 'ECONNABORTED'
        ? 'La risposta sta richiedendo più di 2 minuti: riapri la conversazione tra poco, verrà salvata quando arriva'
        : err.response?.data?.error || err.message || 'Errore di comunicazione';
      setError(errMsg);
      // Remove the optimistic user message if we got an error before sending
      // (only if it's a network/creation error — keep it if the AI just failed)
    } finally {
      setLoading(false);
      setInAttesaPer(null);
    }
  }, [input, loading, onConversationCreated]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isFull = mode === 'full';

  return (
    <div className={`flex flex-col ${isFull ? 'h-full' : 'h-full'}`}>
      {/* Messages area */}
      <div className={`flex-1 overflow-y-auto px-3 py-3 ${isFull ? 'px-4 py-4' : ''}`}>
        {loadingConversation ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-xs text-gray-400">Caricamento...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-400 dark:text-gray-500">
              <p className="text-sm font-medium mb-1">
                {userName ? `Ciao ${userName}!` : 'Assistente UNASCA'}
              </p>
              <p className="text-xs">Chiedimi informazioni su associati, articoli, eventi, statistiche...</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => (
              <AssistantMessage key={i} message={msg} />
            ))}
            {loading && inAttesaPer === conversationId && (
              <AssistantMessage
                message={{ role: 'assistant', content: '', timestamp: '' }}
                isLoading
              />
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-3 mb-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600 text-xs font-medium">
            &times;
          </button>
        </div>
      )}

      {/* Input area */}
      <div className={`border-t border-gray-200 dark:border-gray-700 p-3 ${isFull ? 'p-4' : ''}`}>
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Scrivi un messaggio..."
            rows={1}
            disabled={loading}
            className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 placeholder:text-gray-400"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">
          Shift+Invio per andare a capo
        </p>
      </div>
    </div>
  );
};

export default AssistantChat;
